import React, { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaDownload, FaFileAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { fileService } from '../services/api';
import { formatFileSize, getFileType, getFileExtension, isRawImage } from '../utils/fileUtils';
import { toast } from 'react-toastify';

const FilePreviewModal = ({ 
  isOpen, 
  onClose, 
  file 
}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && file) {
      setLoading(true);
      setError(null);
      setFileUrl(null);
      setTextContent(null);
      
      const fileName = file.Key || file.name;
      const fileType = getFileType(fileName);
      
      // Cargar URL del archivo (sin download para vista previa)
      fileService.getFileUrl(fileName, false)
        .then(response => {
          setFileUrl(response.url);
          setLoading(false); // Parar loading una vez que tenemos la URL
          
          // Para archivos de texto y código, cargar su contenido en paralelo
          const shouldLoadText = (fileType === 'document' && isTextFile(fileName)) || fileType === 'code';
          
          if (shouldLoadText) {
            loadTextContent(fileName);
          }
        })
        .catch(err => {
          setError('Error al cargar el archivo');
          setLoading(false);
          console.error('Error cargando archivo:', err);
        });
    } else if (!isOpen) {
      // Reset states when modal closes
      setFileUrl(null);
      setTextContent(null);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, file]);

  const isTextFile = (fileName) => {
    const textExtensions = ['txt', 'json', 'xml', 'csv', 'md', 'html', 'css', 'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'log'];
    const extension = getFileExtension(fileName);
    return textExtensions.includes(extension);
  };

  const loadTextContent = async (fileName) => {
    try {
      const response = await fileService.getFileContent(fileName);
      setTextContent(response.content);
    } catch (err) {
      console.error('Error loading text content:', err);
      setTextContent('Error al cargar el contenido del archivo: ' + err.message);
    }
  };

  const handleDownload = async () => {
    const fileName = file?.Key || file?.name;
    try {
      const response = await fileService.getFileUrl(fileName, true); // download=true
      const url = response.url;
      const link = document.createElement('a');
      link.href = url;
      link.download = file.displayName || file.Key || file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast.error('Error al obtener la URL de descarga', { autoClose: 2000 });
    }
  };

  const handleOpenInNewTab = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center space-y-4">
          <FaSpinner className="text-4xl text-blue-500 animate-spin" />
          <p className="text-gray-400">Cargando archivo...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center space-y-4 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      );
    }

    if (!fileUrl) return null;

    const fileName = file?.displayName || file?.Key || file?.name || '';
    const fileType = getFileType(fileName);
    const extension = getFileExtension(fileName);

    switch (fileType) {
      case 'image':
        // Manejar archivos RAW de manera especial
        if (isRawImage(fileName)) {
          return (
            <div className="flex flex-col items-center justify-center space-y-6 p-8 text-center min-h-[400px]">
              {/* Ícono grande de cámara */}
              <div className="text-8xl">📸</div>
              
              {/* Badge RAW */}
              <div className="px-4 py-2 bg-purple-600 rounded-full text-white font-bold text-lg shadow-lg">
                RAW
              </div>
              
              {/* Información del archivo */}
              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-white">
                  ℹ️ Acerca de los archivos RAW
                </h3>
                <p className="text-xl text-gray-300">
                  Archivo de Imagen Profesional
                </p>
                <p className="text-lg text-purple-300 font-semibold">
                  {formatFileSize(file?.Size || file?.size || 0)}
                </p>
              </div>
              
              {/* Explicación */}
              <div className="bg-gray-900 rounded-lg p-6 max-w-md border border-gray-700">
                <h4 className="text-lg font-semibold text-white mb-3">
                  ℹ️ Acerca de los archivos RAW
                </h4>
                <ul className="text-sm text-gray-300 space-y-2 text-left">
                  <li>• Formato de imagen sin procesar</li>
                  <li>• Máxima calidad y flexibilidad de edición</li>
                  <li>• Requiere software especializado</li>
                  <li>• No se puede mostrar directamente en navegadores</li>
                </ul>
              </div>
              
              {/* Recomendaciones */}
              <div className="bg-blue-900/50 rounded-lg p-4 max-w-md border border-blue-700">
                <h4 className="text-white font-semibold mb-2">
                  🛠️ Software recomendado:
                </h4>
                <p className="text-sm text-blue-200">
                  Adobe Lightroom, Camera Raw, Capture One, Luminar, RawTherapee
                </p>
              </div>
              
              {/* Botón de descarga prominente */}
              <button
                onClick={handleDownload}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3 rounded-lg flex items-center space-x-3 transition-all transform hover:scale-105 shadow-lg"
              >
                <FaDownload className="text-lg" />
                <span className="font-semibold">Descargar para Editar</span>
              </button>
            </div>
          );
        }
        
        // Para imágenes normales (JPG, PNG, etc.)
        return (
          <img 
            src={fileUrl} 
            alt={fileName}
            className="max-w-full max-h-full object-contain rounded-lg"
            onError={(e) => {
              // Fallback si la imagen normal no se puede cargar
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        );

      case 'video':
        return (
          <video 
            controls 
            className="max-w-full max-h-full rounded-lg"
            preload="metadata"
          >
            <source src={fileUrl} type={`video/${extension}`} />
            Tu navegador no soporta la reproducción de video.
          </video>
        );

      case 'audio':
        return (
          <div className="flex flex-col items-center space-y-4">
            <FaFileAlt className="text-6xl text-blue-500" />
            <h4 className="text-lg text-white">{fileName}</h4>
            <audio 
              controls 
              className="w-full max-w-md"
              preload="metadata"
            >
              <source src={fileUrl} type={`audio/${extension}`} />
              Tu navegador no soporta la reproducción de audio.
            </audio>
          </div>
        );

      case 'document':
        if (extension === 'pdf') {
          return (
            <iframe
              src={fileUrl}
              className="w-full h-full min-h-[500px] rounded-lg"
              title={fileName}
            />
          );
        } else if (isTextFile(fileName)) {
          if (textContent !== null) {
            return (
              <div className="w-full h-full max-h-[500px] overflow-auto">
                <pre className="bg-gray-900 p-4 rounded-lg text-sm text-gray-300 whitespace-pre-wrap">
                  {textContent}
                </pre>
              </div>
            );
          } else {
            // Archivo de texto pero aún cargando contenido
            return (
              <div className="flex flex-col items-center space-y-4">
                <FaSpinner className="text-4xl text-blue-500 animate-spin" />
                <p className="text-gray-400">Cargando contenido del archivo...</p>
              </div>
            );
          }
        } else {
          // Documentos binarios como DOC, DOCX
          return (
            <div className="flex flex-col items-center space-y-4 text-center">
              <FaFileAlt className="text-6xl text-blue-500" />
              <h4 className="text-lg text-white">{fileName}</h4>
              <p className="text-gray-400">
                Este tipo de documento ({extension.toUpperCase()}) no se puede previsualizar directamente.
              </p>
              <p className="text-sm text-gray-500">
                Los archivos .docx requieren una aplicación específica para visualizarse.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDownload}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <FaDownload />
                  <span>Descargar</span>
                </button>
              </div>
            </div>
          );
        }

      case 'code':
        if (textContent !== null) {
          return (
            <div className="w-full h-full max-h-[500px] overflow-auto">
              <pre className="bg-gray-900 p-4 rounded-lg text-sm text-gray-300 whitespace-pre-wrap">
                <code>{textContent}</code>
              </pre>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-center space-y-4 text-center">
            <FaFileAlt className="text-6xl text-green-500" />
            <h4 className="text-lg text-white">{fileName}</h4>
            <p className="text-gray-400">Archivo de código</p>
            <div className="flex space-x-3">
              <button
                onClick={handleOpenInNewTab}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <FaExternalLinkAlt />
                <span>Abrir en nueva pestaña</span>
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex flex-col items-center space-y-4 text-center">
            <FaFileAlt className="text-6xl text-gray-500" />
            <h4 className="text-lg text-white">{fileName}</h4>
            <p className="text-gray-400">
              Vista previa no disponible para este tipo de archivo.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleOpenInNewTab}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <FaExternalLinkAlt />
                <span>Abrir en nueva pestaña</span>
              </button>
              <button
                onClick={handleDownload}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <FaDownload />
                <span>Descargar</span>
              </button>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  const fileName = file?.displayName || file?.Key || file?.name || '';
  const fileSize = file?.Size || file?.size || 0;
  const fileType = getFileType(fileName);

  return (
    <div className="fixed inset-0 z-50 overflow-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] border border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-white truncate">
                {fileName}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                {fileSize > 0 && (
                  <span>{formatFileSize(fileSize)}</span>
                )}
                <span className="capitalize">{fileType}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {fileUrl && (
                <>
                  <button
                    onClick={handleOpenInNewTab}
                    className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded"
                    title="Abrir en nueva pestaña"
                  >
                    <FaExternalLinkAlt />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded"
                    title="Descargar"
                  >
                    <FaDownload />
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded"
                title="Cerrar"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-auto max-h-[calc(90vh-100px)]">
            <div className="flex items-center justify-center min-h-[400px]">
              {renderPreviewContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal; 